import React, { useState } from "react";

// Small inline icon set (24x24 grid, stroke only) so the toolbar doesn't depend on image files.
const ICONS = {
  ship: <><path d="M3 17l2 3h14l2-3-9-3z" /><path d="M12 14V4l6 7" /></>,
  recycle: <><path d="M4 20h16" /><path d="M6 20V9l6-4 6 4v11" /><path d="M10 20v-5h4v5" /></>,
  port: <><circle cx="12" cy="5" r="2" /><path d="M12 7v14" /><path d="M5 13a7 7 0 0014 0" /><path d="M8 11h8" /></>,
  light: <><path d="M9 21l1-12h4l1 12z" /><path d="M10 9l2-4 2 4" /><path d="M4 8h3M17 8h3" /></>,
  viewer: <><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 15l5-4 5 4 3-2 5 4" /></>,
  locate: <><circle cx="12" cy="12" r="3" /><path d="M12 2v4M12 18v4M2 12h4M18 12h4" /></>,
  coords: <><path d="M12 21s-6-5.5-6-11a6 6 0 0112 0c0 5.5-6 11-6 11z" /><circle cx="12" cy="10" r="2" /></>,
  measure: <><path d="M3 17L17 3l4 4L7 21z" /><path d="M7 13l2 2M10 10l2 2M13 7l2 2" /></>,
  clear: <><path d="M6 6l12 12M18 6L6 18" /></>,
  goto: <><circle cx="11" cy="11" r="6" /><path d="M20 20l-4.5-4.5" /><path d="M11 8v6M8 11h6" /></>,
  radius: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="1.5" /><path d="M12 12l6-5" /></>,
  fit: <><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" /><circle cx="12" cy="12" r="2.5" /></>,
  scale: <><path d="M3 17h18M3 13v8M21 13v8M8 15v2M13 15v2M18 15v2" /></>,
  fullscreen: <><path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" /></>,
  filter: <><path d="M3 5h18l-7 8v6l-4-2v-4z" /></>,
  timeline: <><path d="M3 12h18" /><circle cx="8" cy="12" r="2.2" /><circle cx="16" cy="12" r="2.2" /><path d="M5 19h14" /></>,
  nearby: <><circle cx="8" cy="12" r="2" /><path d="M13 7.5a6 6 0 010 9M16.5 4.5a10.5 10.5 0 010 15" /></>,
  dice: <><rect x="4" y="4" width="16" height="16" rx="3" /><path d="M9 9h.01M15 9h.01M9 15h.01M15 15h.01M12 12h.01" /></>,
  download: <><path d="M12 4v11" /><path d="M7 11l5 5 5-5" /><path d="M5 20h14" /></>,
};

function Icon({ name }) {
  return <svg viewBox="0 0 24 24" aria-hidden="true">{ICONS[name]}</svg>;
}

function Tool({ icon, label, pressed, onClick, href, disabled, note, keep }) {
  const body = (
    <>
      <Icon name={icon} />
      <span className="tool-label">{label}</span>
      {note && <span className="tool-state">{note}</span>}
    </>
  );
  if (href) return <a className="tool" href={href} title={label} data-keep={keep ? "" : undefined}>{body}</a>;
  return (
    <button
      type="button"
      className="tool"
      title={label}
      data-keep={keep ? "" : undefined}
      disabled={disabled}
      aria-pressed={pressed === undefined ? undefined : pressed}
      onClick={onClick}
    >
      {body}
    </button>
  );
}

export default function MapTools({
  showMarkers, onToggleMarkers,
  showYards, onToggleYards, yardCount,
  filtersOpen, onToggleFilters, filtersActive, timelineOpen, onToggleTimeline,
  showCoordinates, onToggleCoordinates,
  measureMode, onToggleMeasure, canClearMeasure, onClearMeasure,
  gotoOpen, onToggleGoto,
  radiusMode, onToggleRadius,
  onFitAll,
  showScale, onToggleScale,
  fullscreen, onToggleFullscreen,
  onLocate,
  nearbyOpen, onToggleNearby, onSurprise,
  mapStyle, mapStyles, onMapStyle,
}) {
  // On phones the toolbar is a bottom sheet opened from a floating button (on desktop it's the slim bar on the right).
  const [open, setOpen] = useState(false);
  const canFullscreen = typeof document !== "undefined" && !!(document.fullscreenEnabled && document.documentElement.requestFullscreen);
  const onNavClick = (e) => {
    const t = e.target.closest(".tool");
    if (t && !t.disabled && !t.hasAttribute("data-keep")) setOpen(false);
  };

  return (
    <>
    <button type="button" className="tools-fab" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-controls="map-tools">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h10M18 7h2M4 17h2M10 17h10" /><circle cx="16" cy="7" r="2" /><circle cx="8" cy="17" r="2" /></svg>
      <span>Tools</span>
    </button>
    {open && <div className="tools-backdrop" onClick={() => setOpen(false)} />}
    <nav id="map-tools" className={`right-sidebar${open ? " open" : ""}`} aria-label="Map tools" onClick={onNavClick}>
      <div className="tools-sheet-head">
        <strong>Map tools</strong>
        <button type="button" className="tools-sheet-close" onClick={() => setOpen(false)} aria-label="Close map tools">×</button>
      </div>
      <div className="tool-group">
        <div className="tool-group-title">Layers</div>
        <Tool keep icon="ship" label="Shipwrecks" pressed={showMarkers} onClick={onToggleMarkers} note={showMarkers ? "On" : "Off"} />
        <Tool keep icon="recycle" label="Recycling centers" pressed={showYards} onClick={onToggleYards}
              note={showYards ? (yardCount ? String(yardCount) : "…") : "Off"} />
        <Tool icon="port" label="Ports" disabled note="Soon" />
        <Tool icon="light" label="Lighthouses" disabled note="Soon" />
      </div>

      <div className="tool-group">
        <div className="tool-group-title">Explore</div>
        <Tool icon="nearby" label="Wrecks near me" pressed={nearbyOpen} onClick={onToggleNearby} />
        <Tool icon="dice" label="Surprise me" onClick={onSurprise} />
      </div>

      <div className="tool-group">
        <div className="tool-group-title">Filter</div>
        <Tool keep icon="filter" label="Filters" pressed={filtersOpen} onClick={onToggleFilters} note={filtersActive ? "On" : undefined} />
        <Tool keep icon="timeline" label="Timeline" pressed={timelineOpen} onClick={onToggleTimeline} />
      </div>

      <div className="tool-group">
        <div className="tool-group-title">Measure &amp; locate</div>
        <Tool icon="measure" label="Measure distance" pressed={measureMode} onClick={onToggleMeasure} />
        {canClearMeasure && <Tool icon="clear" label="Clear measurement" onClick={onClearMeasure} />}
        <Tool icon="radius" label="Wrecks in radius" pressed={radiusMode} onClick={onToggleRadius} />
        <Tool icon="coords" label="Show coordinates" pressed={showCoordinates} onClick={onToggleCoordinates} note={showCoordinates ? "On" : "Off"} />
        <Tool icon="goto" label="Go to coordinates" pressed={gotoOpen} onClick={onToggleGoto} />
        <Tool icon="locate" label="My location" onClick={onLocate} />
      </div>

      <div className="tool-group">
        <div className="tool-group-title">View</div>
        <Tool icon="fit" label="Fit all wrecks" onClick={onFitAll} />
        <Tool keep icon="scale" label="Scale bar" pressed={showScale} onClick={onToggleScale} note={showScale ? "On" : "Off"} />
        {canFullscreen && <Tool icon="fullscreen" label="Fullscreen map" pressed={fullscreen} onClick={onToggleFullscreen} />}
        <Tool icon="viewer" label="Wreck viewer" href="/viewer" />
      </div>

      <div className="tool-group">
        <div className="tool-group-title">Open data</div>
        <Tool keep icon="download" label="Download GeoJSON" href="/api/export?format=geojson" />
        <Tool keep icon="download" label="Download CSV" href="/api/export?format=csv" />
      </div>

      <div className="tool-group">
        <div className="tool-group-title">Basemap</div>
        <div className="tool-select">
          <select value={mapStyle} onChange={(e) => onMapStyle(e.target.value)} aria-label="Basemap">
            {Object.keys(mapStyles).map((k) => <option key={k} value={k}>{mapStyles[k].name}</option>)}
          </select>
        </div>
      </div>
    </nav>
    </>
  );
}
